"use client";

import {
  Cell,
  Pie,
  PieChart as RechartsPieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { ChartContainer, type ChartConfig } from "./ChartContainer";

export type PieChartData = Record<string, string | number>;

export type PieChartProps = {
  /** Chart data (array of objects, e.g. [{ name: "Chrome", value: 100 }]) */
  data: PieChartData[];
  /** Chart configuration */
  config: ChartConfig;
  /** Data key for segment value (e.g. "value", "visitors") */
  dataKey: string;
  /** Data key for segment name/label (e.g. "name", "browser") */
  nameKey: string;
  /** Inner radius for donut style (0 = full pie) */
  innerRadius?: number;
  /** Outer radius */
  outerRadius?: number;
  /** Show tooltip (default: true) */
  showTooltip?: boolean;
  /** Hide label in tooltip (default: false) */
  hideTooltipLabel?: boolean;
  /** Chart container className */
  className?: string;
};

export function PieChart({
  data,
  config,
  dataKey,
  nameKey,
  innerRadius = 0,
  outerRadius = 80,
  showTooltip = true,
  hideTooltipLabel = false,
  className,
}: PieChartProps) {
  return (
    <ChartContainer config={config} className={className}>
      <ResponsiveContainer width="100%" height="100%">
        <RechartsPieChart>
          {showTooltip && (
            <Tooltip
              cursor={false}
              contentStyle={{
                backgroundColor: "var(--background)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius)",
              }}
              formatter={(value, name) => {
                const v = typeof value === "number" ? value : 0;
                const label = config[String(name)]?.label ?? String(name);
                return hideTooltipLabel
                  ? [v.toLocaleString()]
                  : [v.toLocaleString(), label];
              }}
            />
          )}
          <Pie
            data={data}
            dataKey={dataKey}
            nameKey={nameKey}
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
          >
            {data.map((entry, index) => {
              const name = String(entry[nameKey]);
              const color =
                config[name]?.color ??
                `var(--chart-${((index % 5) + 1) as 1 | 2 | 3 | 4 | 5})`;
              return <Cell key={name} fill={color} />;
            })}
          </Pie>
        </RechartsPieChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
}
